/**
 * Per-node measurements for the trace graph, computed in one pass.
 *
 * Replaces a per-node subtree walk, where each node summed its own descendants. That shape costs
 * O(n × depth) rather than O(n): measured at ~4 ms for 8 000 spans, since `TraceTreeBuilder` caps
 * depth at 32. It was **not** the cause of the browser stalling on a large trace — mounting one
 * component per span is — so this module is a tidiness and safety win, not the performance fix.
 *
 * What it does buy:
 *  - one traversal shared by the graph and by `TraceView`'s flat span list, instead of two
 *  - termination on a cyclic tree, which the recursive version would hang on
 *  - no stack limit: the recursive version overflows at ~15 000 deep, and while the API caps depth
 *    at 32 today, rendering code should not inherit that as a load-bearing assumption
 *
 * Deliberately free of Vue and the DOM so it can be measured directly in a unit test.
 */
import type { TraceNode, TraceSpan } from '@/backend/traces.types'

export interface TraceNodeMetrics {
  depth: number

  /** This node plus every descendant, so a collapsed node can report what it is hiding. */
  subtreeSize: number
}

export interface TraceGraphModel {
  /**
   * Keyed by node identity rather than `spanKey`: the key is only guaranteed unique for spans the
   * backend produced, and a lookup that silently collides would report the wrong subtree size.
   */
  metrics: Map<TraceNode, TraceNodeMetrics>

  /** Pre-order, so callers needing a flat span list do not walk the tree a second time. */
  spans: TraceSpan[]

  /**
   * Each node's parent, which is what lets a deep-linked span reveal itself: its ancestors have to be
   * expanded, and they can only be found by walking upwards.
   *
   * Absent for roots. Records the first parent seen, so a shared subtree reached twice is attributed
   * to whichever branch found it first — consistent with `subtreeSize` counting it once.
   */
  parents: Map<TraceNode, TraceNode>
}

/**
 * The `spanKey`s of every ancestor of the span named by `spanKey`, excluding the span itself.
 *
 * Used to expand a collapsed branch around a deep-linked selection: a job scheduled by another job
 * can sit well below the collapse depth, and a highlighted node inside a collapsed subtree is not
 * merely unhelpful — it is invisible.
 */
export function findAncestorKeys(model: TraceGraphModel, spanKey: string | undefined): Set<string> {
  const keys = new Set<string>()

  if (!spanKey) {
    return keys
  }

  let node = [...model.metrics.keys()].find((candidate) => candidate.span.spanKey === spanKey)

  // Guards a cyclic tree, which `buildTraceGraphModel` tolerates rather than rejects. Seeded with the
  // starting node so a cycle back onto it stops rather than listing the selection as its own ancestor.
  const seen = new Set<string>([spanKey])

  while (node) {
    const parent = model.parents.get(node)

    if (!parent || seen.has(parent.span.spanKey)) break

    seen.add(parent.span.spanKey)
    keys.add(parent.span.spanKey)
    node = parent
  }

  return keys
}

/**
 * Walks the tree twice — once down to record depth, once back up to accumulate sizes — which is
 * O(n) in total.
 *
 * Iterative rather than recursive: the backend caps depth at 32, but a cycle that slipped through
 * would blow the stack instead of terminating, and this is rendering code.
 */
export function buildTraceGraphModel(roots: TraceNode[]): TraceGraphModel {
  const metrics = new Map<TraceNode, TraceNodeMetrics>()
  const spans: TraceSpan[] = []

  /** Pre-order, so a parent always precedes its children and the reverse pass sees children first. */
  const order: TraceNode[] = []
  const parents = new Map<TraceNode, TraceNode>()

  const stack: { node: TraceNode; depth: number }[] = []

  for (let i = roots.length - 1; i >= 0; i--) {
    stack.push({ node: roots[i], depth: 0 })
  }

  while (stack.length > 0) {
    const { node, depth } = stack.pop()!

    // A node reached twice means a cycle or a shared subtree. Counting it once keeps the totals
    // honest and guarantees termination.
    if (metrics.has(node)) continue

    metrics.set(node, { depth, subtreeSize: 1 })
    order.push(node)
    spans.push(node.span)

    const children = node.children ?? []

    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i]
      if (!parents.has(child)) parents.set(child, node)
      stack.push({ node: child, depth: depth + 1 })
    }
  }

  for (let i = order.length - 1; i >= 0; i--) {
    const node = order[i]
    const parent = parents.get(node)
    if (!parent) continue

    const parentMetrics = metrics.get(parent)
    const nodeMetrics = metrics.get(node)

    if (parentMetrics && nodeMetrics) {
      parentMetrics.subtreeSize += nodeMetrics.subtreeSize
    }
  }

  return { metrics, spans, parents }
}
